import React, { useState, useEffect } from "react";
import { Button, Modal, Space, Tabs, Table, message, theme } from "antd";
import { useTokens } from "../_actions/finance";
import { useItemsFine } from "./../_actions/app/items";
import TOKEN_ICON from "../media/imgs/token_test.png";
import styles from "./styles/TokenManager.module.css";
import { getTime } from "../_helpers/app/dt";
import {
    useCreateTopup,
    useVerifyTopup,
} from "../_actions/mutations/subscription";
import { useCurrentUser } from "../_actions/auth";

const RAZORPAY_KEY = "rzp_live_RiGIXNKU65WT3j";

function loadRazorpayScript() {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
}

export default function TokenManagerModal({
    trigger,
    selected_org,
    open: openProp,
    onClose,
}) {
    const [open, setOpen] = useState(false);
    const [activeKey, setActiveKey] = useState("topup");
    const { isLoading: tokensLoading, data: tokensData } = useTokens(
        selected_org?.id,
    );
    // sync controlled prop if provided
    useEffect(() => {
        if (typeof openProp !== "undefined") {
            setOpen(openProp);
        }
    }, [openProp]);

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            setOpen(false);
        }
    };

    const showHistory = activeKey === "history";

    return (
        <>
            {/* <span onClick={() => setOpen(true)}>{trigger}</span> */}
            {typeof openProp === "undefined" && (
                <span onClick={() => setOpen(true)}>{trigger}</span>
            )}

            <Modal
                title="Billing Information"
                open={open}
                onCancel={handleClose}
                footer={null}
                maskClosable={false}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    <div className={styles.box_wrapper}>
                        <img
                            className={styles.token_img}
                            src={TOKEN_ICON}
                            alt="token"
                        />
                        <div className={styles.token_info}>
                            <p>Available Tokens</p>
                            <h1>{tokensData?.[0]?.count ?? 0}</h1>
                        </div>
                    </div>

                    <Tabs
                        activeKey={activeKey}
                        onChange={(key) => setActiveKey(key)}
                        type="card"
                        items={[
                            {
                                key: "topup",
                                label: "Top Up",
                                children: <TopupBox orgId={selected_org?.id} />,
                            },
                            {
                                key: "earn",
                                label: "Earn",
                                children: <div>Earn</div>,
                            },
                            {
                                key: "history",
                                label: "History",
                                children: (
                                    <HistoryPanel
                                        show={showHistory}
                                        selected_org={selected_org}
                                    />
                                ),
                            },
                        ]}
                    />
                </Space>
            </Modal>
        </>
    );
}

const TOPUP_PLANS = [
    { label: "5", value: "topup_5", tokens: 3 },
    { label: "100", value: "topup_100", tokens: 60 },
    { label: "200", value: "topup_200", tokens: 120 },
    { label: "500", value: "topup_500", tokens: 300 },
    { label: "1000", value: "topup_1000", tokens: 600 },
];

function PreviewCard({ selected_amount }) {
    return (
        <div className={styles.preview_card}>
            <img className={styles.token_img} src={TOKEN_ICON} alt="token" />
            <div className={styles.preview_info}>
                <h3>{selected_amount?.tokens} Tokens</h3>
                <p>Price: ₹{selected_amount?.label}</p>
            </div>
        </div>
    );
}

function TopupBox({ orgId }) {
    const { token } = theme.useToken();
    const { data: currentUser } = useCurrentUser();
    const [selected_amount, setSelectedAmount] = useState(TOPUP_PLANS[3]);
    const [loadingPreset, setLoadingPreset] = useState(null);

    const verifyTopup = useVerifyTopup(
        (data) => {
            setLoadingPreset(null);
            if (data.status === "topup_successful") {
                message.success(
                    `${data.tokens_added} tokens added! New balance: ${data.new_balance}`,
                );
            } else if (data.status === "already_processed") {
                message.info("This payment was already processed.");
            }
        },
        (error) => {
            setLoadingPreset(null);
            message.error(
                error?.response?.data?.detail ||
                "Payment verification failed. Contact support.",
            );
        },
    );

    const createTopup = useCreateTopup(
        async (data) => {
            const loaded = await loadRazorpayScript();
            if (!loaded) {
                setLoadingPreset(null);
                message.error(
                    "Razorpay failed to load. Check your connection.",
                );
                return;
            }
            const options = {
                key: RAZORPAY_KEY,
                order_id: data.order_id,
                amount: data.amount * 100,
                currency: "INR",
                name: "Qureal AI",
                description: `Add ${data.tokens_to_add} tokens`,
                image: "https://api.qureal.com/passets/c90e4024-5d6a-4df0-b713-9362a6083eb9.png",
                handler: (response) => {
                    verifyTopup.mutate({
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_signature: response.razorpay_signature,
                    });
                },
                modal: { ondismiss: () => setLoadingPreset(null) },
                theme: { color: token.colorPrimary },
            };
            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", () => {
                setLoadingPreset(null);
                message.error("Payment failed. Please try again.");
            });
            rzp.open();
        },
        (error) => {
            setLoadingPreset(null);
            message.error(
                error?.response?.data?.detail ||
                "Failed to create top-up order.",
            );
        },
    );

    const handleBuy = () => {
        if (loadingPreset) return;
        setLoadingPreset(selected_amount.value);
        createTopup.mutate({
            org_id: orgId,
            account_id: currentUser?.id,
            preset: selected_amount.value,
        });
    };

    return (
        <div>
            <div className={styles.plans_container}>
                {TOPUP_PLANS.map((item) => (
                    <div
                        key={item.value}
                        className={`${styles.plan_item} ${item.value === selected_amount.value
                            ? styles.plan_item_selected
                            : ""
                            }`}
                        onClick={() => setSelectedAmount(item)}
                    >
                        {item.label}
                    </div>
                ))}
            </div>
            <PreviewCard selected_amount={selected_amount} />
            <Button
                type="primary"
                block
                loading={!!loadingPreset}
                onClick={handleBuy}
            >
                Buy Now ₹{selected_amount?.label}
            </Button>
        </div>
    );
}

function HistoryPanel({ show, selected_org }) {
    const enabled = !!show && !!selected_org?.id;

    const params = enabled
        ? `filter[organization][id][_eq]=${selected_org?.id}&sort=-date_created&fields=id,token_consumed,medium,date_created,user_created.id,user_created.first_name,user_created.last_name`
        : null;

    const itemsQuery = useItemsFine("qdb_creation_history", params, enabled);

    useEffect(() => {
        if (!enabled) return;
        console.log("Selected org details:", selected_org);
        console.log("History params:", params);
        console.log("itemsQuery:", {
            error: itemsQuery.error,
            raw: itemsQuery?.data,
            rows: itemsQuery?.data?.data ?? itemsQuery?.data ?? [],
        });
    }, [enabled, params, itemsQuery]);

    const rows = itemsQuery?.data?.data ?? itemsQuery?.data ?? [];

    const columns = [
        {
            title: "Token consumed",
            dataIndex: "token_consumed",
            key: "token_consumed",
            render: (val, record) =>
                val ?? record?.token_consumed ?? record?.token ?? "-",
        },
        {
            title: "Medium",
            dataIndex: "medium",
            key: "medium",
            render: (val) => val ?? "-",
        },
        {
            title: "User created",
            dataIndex: "user_created",
            key: "user_created",
            render: (user) => {
                if (!user) return "-";
                if (typeof user === "string") {
                    return user;
                }
                const fullName = `${user.first_name || ""} ${user.last_name || ""
                    }`.trim();
                return fullName || user.id || "-";
            },
        },
        {
            title: "Date created",
            dataIndex: "date_created",
            key: "date_created",
            render: (val) => (val ? getTime(val, "r", true) : "-"),
        },
    ];

    return (
        <div style={{ width: "100%" }}>
            <Table
                rowKey={(record) =>
                    record?.id ?? record?.date_created ?? Math.random()
                }
                columns={columns}
                dataSource={rows}
                loading={itemsQuery.isLoading}
                pagination={{ pageSize: 5 }}
                locale={{
                    emptyText: itemsQuery.isLoading
                        ? "Loading..."
                        : itemsQuery.isError
                            ? `Error: ${itemsQuery.error?.message ?? "Fetch error"}`
                            : "No history found",
                }}
            />
        </div>
    );
}
